import React from 'react';
import { connect, batch } from 'react-redux';
import classnames from 'classnames';
import { useDropzone } from 'react-dropzone';

import {
  setError,
  addFile,
  setCustomIconFile,
  setCustomIconError,
  uploadCustomIcon,
  deleteCustomIcon,
} from '../actions';
import { displayErrorModal } from '../../../../helpers/swal';

import Info from '../../../../components/Info';
import ActionButtons from './ActionButtons';
import ImageUploader from './ImageUploader';
import ImageSelector from './ImageSelector';

import style from './style.module.scss';

const ImageEditor = ({
  section: {
    label,
    error,
    fileFormat,
    isUploading,
    validationGuideline,
    value,
    customIconData,
    customIconError,
  },
  selectedSectionType,
  setErrorAction,
  setFileAction,
  setCustomIconFileAction,
  setCustomIconErrorAction,
  uploadCustomIconAction,
  deleteCustomIconAction,
}) => {
  const onDrop = (acceptedFiles) => {
    const [inputFile] = acceptedFiles;

    if (!inputFile.type.startsWith('image')) {
      setErrorAction('Only images are supported');
    } else {
      batch(() => {
        setErrorAction(null);
        setFileAction({ file: { inputFile, preview: URL.createObjectURL(inputFile) } });
      });
    }
  };

  const onCustomIconDrop = (acceptedFiles) => {
    const [inputFile] = acceptedFiles;

    if (!inputFile.type.startsWith('image')) {
      setCustomIconErrorAction('Only images are supported');
    } else {
      batch(() => {
        setCustomIconErrorAction(null);
        setCustomIconFileAction({ file: { inputFile, preview: URL.createObjectURL(inputFile) } });
      });
      uploadCustomIconAction(inputFile);
    }
  };

  const formatToMime = (formats) => {
    if (!formats) return {};
    const formatsArray = Array.isArray(formats) ? formats : [formats];

    const mimeMapping = {
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.webp': 'image/webp',
      '.svg': 'image/svg+xml',
    };

    const mimeTypes = formatsArray.reduce((acc, format) => {
      const mimeType = mimeMapping[format] || format;
      const extension = format?.startsWith('.') ? format : `.${format}`;
      acc[mimeType] = [extension];
      return acc;
    }, {});

    return mimeTypes;
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
  } = useDropzone({
    onDrop,
    accept: formatToMime(fileFormat),
  });

  const {
    getRootProps: getCustomIconRootProps,
    getInputProps: getCustomIconInputProps,
  } = useDropzone({
    onDrop: onCustomIconDrop,
    accept: {
      'image/svg+xml': ['.svg'],
      'image/png': ['.png'],
    },
  });

  const dropZoneProps = getRootProps();
  const { onClick: selectFile } = dropZoneProps;

  const customIconDropZoneProps = getCustomIconRootProps();
  const { onClick: selectCustomIconFile } = customIconDropZoneProps;

  const dropZoneClassName = classnames(
    'w-100 text-center',
    style.dropzone,
    {
      'border-primary': isDragActive || isDragAccept,
      [style.disabled]: isUploading,
    },
  );

  if (error) {
    displayErrorModal({ text: error });
    setErrorAction(null);
  }

  if (customIconError) {
    displayErrorModal({ text: customIconError });
    setCustomIconErrorAction(null);
  }

  const isCustomIconSelected = selectedSectionType === 'select' && value === 'custom';
  const hasCustomIconUploaded = customIconData?.srsIconUrl;

  return (
    <div className={style.container}>
      <div className={classnames('d-flex', 'justify-content-between flex-row', style.header)}>
        <h3 className="mb-0">
          {label}
          {validationGuideline && <Info content={validationGuideline} className="ml-2" position="right" />}
        </h3>

        <input {...getInputProps()} multiple={false} />
        <input {...getCustomIconInputProps()} multiple={false} />

        <div className="d-flex">
          {selectedSectionType === 'upload' && <ActionButtons selectFile={selectFile} />}
          {isCustomIconSelected && (
            <ActionButtons
              selectFile={selectCustomIconFile}
              hasUploaded={hasCustomIconUploaded}
              customIconData={customIconData}
              deleteCustomIcon={deleteCustomIconAction}
            />
          )}
        </div>
      </div>

      <div className={classnames(
        'd-flex',
        'pt-3',
        'justify-content-center',
        selectedSectionType === 'select' ? style.mainSelector : style.main,
      )}
      >
        {selectedSectionType === 'upload' && (
          <ImageUploader className={dropZoneClassName} dropZoneProps={dropZoneProps} />
        )}
        {selectedSectionType === 'select' && <ImageSelector />}
      </div>
    </div>
  );
};

export default connect(
  ({ graphics: { selectedInd, sections } }) => ({
    selectedSectionType: sections[selectedInd].options ? 'select' : 'upload',
    section: sections[selectedInd],
  }),
  {
    setErrorAction: setError,
    setFileAction: addFile,
    setCustomIconFileAction: setCustomIconFile,
    setCustomIconErrorAction: setCustomIconError,
    uploadCustomIconAction: uploadCustomIcon,
    deleteCustomIconAction: deleteCustomIcon,
  },
)(ImageEditor);
